import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@workspace/ui/components/dialog'
import { EmptyState } from '@workspace/ui/components/empty-state'
import { Play } from 'lucide-react'
import { useRef, useState } from 'react'

export interface VideoPreviewModalProps {
  videoUrl: string
  title?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  startTime?: number
  onTimeUpdate?: (currentTime: number) => void
}

export function VideoPreviewModal({
  videoUrl,
  title,
  open,
  onOpenChange,
  startTime,
  onTimeUpdate,
}: VideoPreviewModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const handleOpenChange = (next: boolean) => {
    if (!next && videoRef.current) {
      onTimeUpdate?.(videoRef.current.currentTime)
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex h-[90vh] max-w-7xl flex-col gap-0 p-0 sm:max-w-7xl">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="truncate pr-8">{title ?? 'Pitch Recording'}</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 bg-black">
          <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
            {isLoading && (
              <EmptyState
                icon={<Play className="size-12 animate-pulse" />}
                title="Loading video..."
                dashed={false}
                className="absolute z-10 border-none"
              />
            )}

            {error && (
              <EmptyState
                icon={<Play className="size-12" />}
                title="Failed to load video"
                dashed={false}
                className="absolute z-10 border-none text-red-400"
              />
            )}

            <video
              ref={videoRef}
              src={videoUrl}
              controls
              autoPlay
              className="h-full w-full object-contain"
              playsInline
              onLoadedMetadata={() => {
                setIsLoading(false)
                if (startTime && videoRef.current) {
                  videoRef.current.currentTime = startTime
                }
              }}
              onError={() => {
                setError('Failed to load video')
                setIsLoading(false)
              }}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
